import Dexie from "dexie";
import { getDb, clearDb, ensureOwnership } from "./DbService";
import type {
  CachedConversation,
  CachedMessage,
  CachedPrompt
} from "./dbTypes";

export const DbConversationService = {
  async getConversations(): Promise<CachedConversation[]> {
    await ensureOwnership();
    const db = getDb();
    return db.conversations.orderBy("updatedAt").reverse().toArray();
  },

  async putConversations(conversations: CachedConversation[]): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    await db.conversations.bulkPut(conversations);
    await db.syncMeta.put({
      key: "conversations",
      lastSyncedAt: Date.now()
    });
  },

  async getMessages(conversationId: string): Promise<CachedMessage[]> {
    await ensureOwnership();
    const db = getDb();
    return db.messages
      .where("[conversationId+createdAt]")
      .between([conversationId, Dexie.minKey], [conversationId, Dexie.maxKey])
      .toArray();
  },

  async putMessages(
    conversationId: string,
    messages: CachedMessage[]
  ): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    const tagged = messages.map((m) => ({ ...m, conversationId }));
    await db.messages.bulkPut(tagged);

    if (tagged.length > 0) {
      const maxCreatedAt = Math.max(...tagged.map((m) => m.createdAt));
      await db.syncMeta.put({
        key: `messages:${conversationId}`,
        lastSyncedAt: maxCreatedAt
      });
    }
  },

  async getLastMessageSync(conversationId: string): Promise<number> {
    await ensureOwnership();
    const db = getDb();
    const meta = await db.syncMeta.get(`messages:${conversationId}`);
    return meta?.lastSyncedAt ?? 0;
  },

  async getLastConversationsSync(): Promise<number> {
    await ensureOwnership();
    const db = getDb();
    const meta = await db.syncMeta.get("conversations");
    return meta?.lastSyncedAt ?? 0;
  },

  async setLastConversationsSync(ts: number): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    await db.syncMeta.put({ key: "conversations", lastSyncedAt: ts });
  },

  async deleteMessage(messageId: string): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    await db.messages.delete(messageId);
  },

  async deleteConversationsNotIn(keepIds: Set<string>): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    const all = await db.conversations.toArray();
    const toDelete = all.filter((c) => !keepIds.has(c.id)).map((c) => c.id);
    if (toDelete.length > 0) {
      await db.conversations.bulkDelete(toDelete);
    }
  },

  async deleteMessagesNotIn(
    conversationId: string,
    keepIds: Set<string>
  ): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    const all = await db.messages
      .where("[conversationId+createdAt]")
      .between([conversationId, Dexie.minKey], [conversationId, Dexie.maxKey])
      .toArray();
    const toDelete = all.filter((m) => !keepIds.has(m.id)).map((m) => m.id);
    if (toDelete.length > 0) {
      await db.messages.bulkDelete(toDelete);
    }
  },

  async trimMessages(conversationId: string, maxCount: number): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    const all = await db.messages
      .where("[conversationId+createdAt]")
      .between([conversationId, Dexie.minKey], [conversationId, Dexie.maxKey])
      .toArray();
    if (all.length <= maxCount) {
      return;
    }
    const sorted = all.sort((a, b) => b.createdAt - a.createdAt);
    const toDelete = sorted.slice(maxCount).map((m) => m.id);
    await db.messages.bulkDelete(toDelete);
  },

  async getPrompts(conversationId: string): Promise<CachedPrompt[]> {
    await ensureOwnership();
    const db = getDb();
    return db.prompts.where("conversationId").equals(conversationId).toArray();
  },

  async putPrompt(prompt: CachedPrompt): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    await db.prompts.put(prompt);
  },

  async resolvePromptsByType(
    conversationId: string,
    eventType: string,
    selectedValue: string
  ): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    const matching = await db.prompts
      .where("conversationId")
      .equals(conversationId)
      .filter((p) => p.eventType === eventType && p.selectedValue === null)
      .toArray();
    if (matching.length > 0) {
      await db.prompts.bulkPut(matching.map((p) => ({ ...p, selectedValue })));
    }
  },

  async deletePromptsForConversation(conversationId: string): Promise<void> {
    await ensureOwnership();
    const db = getDb();
    const ids = await db.prompts
      .where("conversationId")
      .equals(conversationId)
      .primaryKeys();
    if (ids.length > 0) {
      await db.prompts.bulkDelete(ids);
    }
  },

  async clearAll(): Promise<void> {
    await clearDb();
  }
};
